import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../axiosInstance";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
} from "@mui/material";

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  // States
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // For controlling the form
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true); // token check in progress
  const [isTokenValid, setIsTokenValid] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid or expired link.");
      setIsCheckingToken(false);
      return;
    }

    // Immediately check the token with the back-end
    async function checkToken() {
      try {
        await axiosInstance.get(`/api/auth/check-reset-token?token=${token}`);
        // If success, token is valid
        setIsTokenValid(true);
      } catch (err) {
        console.error("checkResetToken error:", err);
        // If error, we assume invalid or expired
        const msg = err.response?.data?.message || "Invalid or expired link.";
        setError(msg);
      } finally {
        setIsCheckingToken(false);
      }
    }

    checkToken();
  }, [token]);

  async function handleSubmit() {
    setError(null);
    setSuccess(null);

    if (!newPassword || !confirmPassword) {
      setError("Please enter your new password and confirm it.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.post("/api/auth/reset-password", {
        token,
        newPassword,
      });
      setSuccess("Password reset successful! You can now log in with your new password.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("reset-password error:", err);
      const msg = err.response?.data?.message || "Failed to reset password.";
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("invalid")) {
        setError("Your reset link is expired or invalid. Please close this window and request a new link.");
      } else {
        setError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

    // Attempt to close the tab/window
    function handleCloseTab() {
      // Some browsers block window.close() unless opened by window.open()
      window.close();
    }
  
    // Fallback: navigate to login if window.close() is blocked
    function handleGoToLogin() {
      navigate("/");
    }

  return (
    <Box
      sx={{
        maxWidth: 400,
        margin: "50px auto",
        border: "1px solid #ccc",
        borderRadius: 2,
        p: 3,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Reset Your Password
      </Typography>

      {/* Show message if we're checking the token */}
      {isCheckingToken && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Validating your reset link...
        </Alert>
      )}

      {/* If we have an error, show it */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <p>Please try again</p>
        </Alert>
      )}

      {/* If success, show success */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Only show form if token check is done, there's no error, and token is valid */}
      {!isCheckingToken && !error && isTokenValid && !success && (
        <Stack spacing={2}>
          <TextField
            label="New Password"
            type="password"
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={isSubmitting}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Resetting..." : "Submit"}
          </Button>
        </Stack>
      )}

      {/* If success, you can add "Close Tab" or "Go to Login" or "openAuthDrawer('login')" here */}
      {success && (
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleCloseTab}>
              Close Tab
            </Button>
            <Button variant="outlined" onClick={handleGoToLogin}>
              Go to Login
            </Button>
          </Stack>
      )}
    </Box>
  );
}

export default ResetPasswordPage;
